import React from "react";
import Hero from "@/components/Hero";
import CTABanner from "@/components/CTABanner";
import AppDownload from "@/components/AppDownload";
import SectionHeading from "@/components/SectionHeading";
import FeatureCard from "@/components/FeatureCard";
import Reveal from "@/components/Reveal";
import Chevron from "@/components/Chevron";
import { useT } from "@/i18n/LanguageProvider";
import {
  MapPin, Route, ScanLine, BarChart3, Bell, Users, Layers, Zap, ShieldCheck, ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";

const PILLAR_ICONS = [Layers, Zap, ShieldCheck];
const HOME_FEATURE_ICONS = [MapPin, Route, ScanLine, BarChart3, Bell, Users];

export default function Home() {
  const t = useT();
  const pillars = (t.pillars.items || []).map((p, i) => ({ ...p, icon: PILLAR_ICONS[i] }));
  const homeFeatures = (t.homeFeatures.items || []).map((f, i) => ({ ...f, icon: HOME_FEATURE_ICONS[i] }));

  return (
    <>
      <Hero />

      {/* Core pillars — dark navy section */}
      <section className="bg-delfi-navy text-white py-24 md:py-32 relative overflow-hidden" data-testid="home-pillars-section">
        <div className="chevron-bg left-[-14%] top-[-36%] opacity-[0.06]" aria-hidden="true">›</div>
        <div className="delfi-container relative">
          <Reveal>
            <SectionHeading dark eyebrow={t.pillars.eyebrow} title={t.pillars.title} description={t.pillars.description} />
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {pillars.map((p, i) => (
              <FeatureCard key={i} dark icon={p.icon} title={p.title} description={p.description} delay={i * 120} />
            ))}
          </div>
        </div>
      </section>

      {/* Features preview — cloud grey */}
      <section className="bg-delfi-cloud py-24 md:py-32" data-testid="home-features-section">
        <div className="delfi-container">
          <Reveal>
            <SectionHeading
              eyebrow={t.homeFeatures.eyebrow}
              title={t.homeFeatures.title}
              description={t.homeFeatures.description}
            />
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {homeFeatures.map((f, i) => (
              <FeatureCard key={i} icon={f.icon} title={f.title} description={f.description} delay={i * 80} />
            ))}
          </div>
          <Reveal delay={120} className="mt-14 flex justify-center">
            <Link to="/features" className="btn-delfi-secondary" data-testid="home-view-features-link">
              {t.common.viewAllFeatures} <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Quote section */}
      <section className="bg-white py-24 md:py-32 relative overflow-hidden" data-testid="home-quote-section">
        <div className="chevron-bg right-[-20%] top-[-20%] opacity-[0.06]" aria-hidden="true">›</div>
        <div className="delfi-container relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 text-delfi-red text-xs tracking-[0.2em] uppercase font-semibold justify-center">
              <Chevron size={14} /> {t.homeQuote.eyebrow}
            </div>
            <blockquote className="mt-6 text-delfi-navy text-2xl md:text-4xl font-semibold leading-snug tracking-tight">
              “{t.homeQuote.quote1} <span className="text-delfi-red">{t.homeQuote.quote2}</span>{" "}
              {t.homeQuote.quote3}”
            </blockquote>
            <div className="mt-8 flex items-center justify-center gap-3 text-delfi-navy/60 text-sm">
              <span className="w-10 h-[1px] bg-delfi-red" />
              <span className="font-semibold tracking-wide uppercase">{t.homeQuote.attribution}</span>
              <span className="w-10 h-[1px] bg-delfi-red" />
            </div>
          </div>
        </div>
      </section>

      <AppDownload />

      <CTABanner />
    </>
  );
}
